/**
 * File: category_page.tsx
 * Description: Public-facing page that displays the list of articles belonging to a single FAQ category,
 * identified by the `idcategoria` query parameter in the URL.
 */

import {
  Box,
  Typography,
  useTheme,
  Link,
} from "@mui/material";
import { Link as RouterLink } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import Header from "@/components/global/header";
import { useCategoriesContext } from "@/context/public/categories_context";
import { ColorsPalletTheme } from "@/constants/colors_pallet_theme";
import BreadcrumbNavegation from "@/components/public/breadcrumb_navegation";

/**
 * Page component that renders the articles of a specific FAQ category.
 *
 * Reads the `idcategoria` query parameter from the URL to identify which category to display.
 * While the categories data is loading, nothing is rendered in the main area.
 * Each article is shown as a navigable link alongside its last-updated date.
 *
 * @returns {JSX.Element} The category detail page with a list of its articles.
 */
function CategoriesPage() {

  const {categories, isLoading} = useCategoriesContext()

  // Main App Theme
  const theme = useTheme();
  const colors = ColorsPalletTheme(theme.palette.mode);

  const [searchParams] = useSearchParams();
  const idCategory = searchParams.get('idcategoria');

  const mainCategory = categories.find((category) => (category.id === Number(idCategory)))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh'}}>
      <Header />
      <BreadcrumbNavegation items={[{label: String(mainCategory?.title), to:`/categoria?idcategoria=${String(mainCategory?.id)}`}]}/>
      <Box
        component="main"
        sx={{
          display: 'flex',
          justifyContent: "center",
          p: 3,
        }}
      >
        {
          isLoading && mainCategory ?
          <></>
          :
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: "100%"}}>
          <Typography variant="h3">{mainCategory?.title}</Typography>
          {
            // Reverse the articles array so the most recently added article appears first.
           mainCategory?.articles.toReversed().map((article) => (
              <Box sx={{ width: {xs:'100%' , sm:'40vw'}, display:"flex", flexDirection: 'column' }}>
                <Link
                    component={RouterLink}
                    to={`/articulo?idcategoria=${String(mainCategory.id)}&idarticulo=${String(article.id)}`}
                    sx={{color: colors.gray[100], width: 'fit-content'}}
                    underline="hover"
                >
                    <Typography sx={{ fontSize:16 }}>{article.title}</Typography>
                </Link>
                <Typography variant="body1" sx={{color: colors.gray[100], alignSelf:"flex-end", paddingTop:1, }}>
                    Última Actualización: {new Date(article.creation_date).toLocaleDateString('es-ES')}
                </Typography>
                <Box sx={{ paddingTop: 1, borderBottom: `2px solid ${colors.gray[800]}`, width: "100%" }}/>
            </Box>
           ))
          }
          </Box>
        }
      </Box>
    </Box>
  );
}

export default CategoriesPage;
